import React, { useId, useMemo } from "react";
import { ladderShape, snakeShape } from "../../functions/boardArt";
import { squareCenter } from "../../functions/boardLayout";

/** Snakes cycle through these so neighbours are easy to tell apart. */
const SNAKE_SKINS = [
  { body: "#4a9d5b", edge: "#255c31", band: "#1f7a34", belly: "#d8f3dc" },
  { body: "#e2b52f", edge: "#8f6a0c", band: "#c9860f", belly: "#fdf1c7" },
  { body: "#7d5cc6", edge: "#422a7d", band: "#5c3ba3", belly: "#e6dcf8" },
  { body: "#d9594a", edge: "#8d2d21", band: "#b83b2c", belly: "#fbdcd4" },
  { body: "#2f9c9c", edge: "#125e5e", band: "#1c7a7a", belly: "#d4f2f2" },
];

const LADDER = { rail: "#d09449", edge: "#7c4a15", rung: "#e8b473" };

const OUTLINE = 0.016;
const RAIL_EDGE = 0.075;
const RAIL_FACE = 0.045;
const RUNG_EDGE = 0.062;
const RUNG_FACE = 0.034;

function Ladder({ layout, link }) {
  const { rails, rungs } = ladderShape(
    squareCenter(layout, Math.min(link.from, link.to)),
    squareCenter(layout, Math.max(link.from, link.to))
  );

  return (
    <g strokeLinecap="round">
      {rungs.map((seg, i) => (
        <line key={`re${i}`} {...seg} stroke={LADDER.edge} strokeWidth={RUNG_EDGE} />
      ))}
      {rails.map((seg, i) => (
        <line key={`le${i}`} {...seg} stroke={LADDER.edge} strokeWidth={RAIL_EDGE} />
      ))}
      {rungs.map((seg, i) => (
        <line key={`rf${i}`} {...seg} stroke={LADDER.rung} strokeWidth={RUNG_FACE} />
      ))}
      {rails.map((seg, i) => (
        <line key={`lf${i}`} {...seg} stroke={LADDER.rail} strokeWidth={RAIL_FACE} />
      ))}
    </g>
  );
}

function buildSnake(layout, link) {
  return {
    link,
    shape: snakeShape(
      squareCenter(layout, link.to),
      squareCenter(layout, link.from)
    ),
    skin: SNAKE_SKINS[link.index % SNAKE_SKINS.length],
  };
}

/** Body only — painted first so crossing snakes never bury a neighbour's head. */
function SnakeBody({ shape, skin, clipId }) {
  return (
    <g>
      <clipPath id={clipId}>
        <path d={shape.outline} />
      </clipPath>
      <path
        d={shape.outline}
        fill={skin.body}
        stroke={skin.edge}
        strokeWidth={OUTLINE}
        strokeLinejoin="round"
      />
      <g clipPath={`url(#${clipId})`}>
        {/* Cylindrical sheen riding one flank of the body. */}
        <path d={shape.highlight} fill={skin.belly} fillOpacity={0.55} stroke="none" />
        {/* Diamond scute pattern down the spine, alternating light/shadow scales. */}
        {shape.scales.map((s, i) => (
          <path
            key={i}
            d={shape.scutePath(s.rx, s.ry)}
            transform={`translate(${s.x} ${s.y}) rotate(${s.angle})`}
            fill={s.alt ? skin.band : skin.edge}
            fillOpacity={s.alt ? 0.85 : 0.28}
          />
        ))}
      </g>
    </g>
  );
}

/** Head + tongue painted in a later pass so mouths stay visible over other bodies. */
function SnakeHead({ shape, skin }) {
  const { head } = shape;
  const browR = head.eye.r;
  return (
    <g transform={`rotate(${head.angle} ${head.x} ${head.y})`}>
      <path
        d={head.tongue}
        fill="none"
        stroke="#d6304a"
        strokeWidth={OUTLINE}
        strokeLinecap="round"
      />
      <path
        d={head.outline}
        fill={skin.body}
        stroke={skin.edge}
        strokeWidth={OUTLINE}
        strokeLinejoin="round"
      />
      {[-1, 1].map((side) => (
        <g key={side}>
          <circle
            cx={head.nostril.x}
            cy={head.y + head.nostril.y * side}
            r={head.nostril.r}
            fill={skin.edge}
          />
          <circle
            cx={head.eye.x}
            cy={head.y + head.eye.y * side}
            r={head.eye.r}
            fill="#fff"
            stroke={skin.edge}
            strokeWidth={OUTLINE * 0.6}
          />
          <circle
            cx={head.eye.x}
            cy={head.y + head.eye.y * side}
            r={head.eye.pupil}
            fill="#1d1d1d"
          />
          <circle
            cx={head.eye.x - head.eye.pupil * 0.3}
            cy={head.y + head.eye.y * side - head.eye.pupil * 0.3}
            r={head.eye.pupil * 0.32}
            fill="#fff"
          />
          <path
            d={`M${head.eye.x - browR} ${head.y + (head.eye.y + browR * 1.4) * side} Q${head.eye.x} ${
              head.y + (head.eye.y + browR * 2.1) * side
            } ${head.eye.x + browR} ${head.y + (head.eye.y + browR * 1.4) * side}`}
            fill="none"
            stroke={skin.edge}
            strokeWidth={OUTLINE * 0.8}
            strokeLinecap="round"
          />
        </g>
      ))}
    </g>
  );
}

/**
 * Every snake and ladder on the board, drawn as one overlay. The viewBox is the
 * grid itself, so the artwork lines up with the cells at any board size.
 *
 * Snakes are drawn in two passes (all bodies, then all heads) so a long body
 * cannot cover another snake's mouth where paths cross.
 */
export default function BoardLinks({ layout }) {
  const uid = useId();
  const snakes = useMemo(
    () =>
      layout.links
        .filter((link) => link.kind === "snake")
        .map((link) => buildSnake(layout, link)),
    [layout]
  );

  return (
    <svg
      className="sl-links"
      viewBox={`0 0 ${layout.cols} ${layout.rows}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      {layout.links
        .filter((link) => link.kind === "ladder")
        .map((link) => (
          <Ladder key={`ladder-${link.from}`} layout={layout} link={link} />
        ))}

      {snakes.map(({ link, shape, skin }) => (
        <SnakeBody
          key={`snake-body-${link.from}`}
          shape={shape}
          skin={skin}
          clipId={`${uid}-snake-${link.from}`}
        />
      ))}
      {snakes.map(({ link, shape, skin }) => (
        <SnakeHead key={`snake-head-${link.from}`} shape={shape} skin={skin} />
      ))}
    </svg>
  );
}
